/**
 * BYO WhatsApp QR pairing (plan whatsapp-byo-flow.md §5.1). Creates a per-user
 * WAHA session on mount, polls the (rotating) QR + status, and calls onConnected
 * once the session reaches WORKING. Offers a pairing-code fallback.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, QrCode } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { IconTile } from '@/components/patterns/icon-tile';
import { InfoNote } from '@/components/patterns/info-note';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { type WahaSessionStatus, wahaSessionApi } from '@/lib/api';
import { qk } from '@/lib/query-client';

interface Props {
  visibility?: 'workspace' | 'private';
  onConnected: (sessionId: string) => void;
  onBack?: () => void;
}

const TERMINAL: WahaSessionStatus[] = ['working', 'failed', 'logged_out', 'error'];

/** WAHA returns a data URL or bare base64 PNG depending on version. */
function qrSrc(qr: string): string {
  return qr.startsWith('data:') ? qr : `data:image/png;base64,${qr}`;
}

export function WahaQrPair({ visibility = 'workspace', onConnected, onBack }: Props) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [showPair, setShowPair] = useState(false);
  const [phone, setPhone] = useState('');
  const [pairCode, setPairCode] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () => wahaSessionApi.create(visibility),
    onSuccess: (res) => setSessionId(res.sessionId),
    onError: () => toast.error("Couldn't start a WhatsApp session. Please try again."),
  });

  // Start a session once, on mount.
  // biome-ignore lint/correctness/useExhaustiveDependencies: run exactly once
  useEffect(() => {
    createMut.mutate();
  }, []);

  const { data } = useQuery({
    queryKey: sessionId ? qk.wahaSession(sessionId) : ['waha-session-idle'],
    queryFn: () => wahaSessionApi.qr(sessionId as string),
    enabled: !!sessionId && !connected,
    refetchInterval: (q) => {
      const s = (q.state.data as { status?: WahaSessionStatus } | undefined)?.status;
      return s && TERMINAL.includes(s) ? false : 2000;
    },
  });

  const status = data?.status ?? 'pending_qr';

  useEffect(() => {
    if (status === 'working' && !connected && sessionId) {
      setConnected(true);
      toast.success('WhatsApp connected!');
      onConnected(sessionId);
    }
  }, [status, connected, onConnected, sessionId]);

  const pairMut = useMutation({
    mutationFn: () => wahaSessionApi.pair(sessionId as string, phone.trim()),
    onSuccess: (res) => setPairCode(res.code),
    onError: () => toast.error("Couldn't get a pairing code. Check the number and try again."),
  });

  function restart() {
    setSessionId(null);
    setConnected(false);
    setPairCode(null);
    setShowPair(false);
    createMut.mutate();
  }

  const failed = status === 'failed' || status === 'error' || createMut.isError;

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border p-8">
        <IconTile tone="neutral">
          <QrCode className="h-5 w-5" />
        </IconTile>
        <h2 className="mt-4 text-lg font-semibold">Connect your WhatsApp</h2>
        <p className="mt-1.5 text-[14px] text-muted-foreground">
          Open WhatsApp on your phone →{' '}
          <span className="font-medium">Settings → Linked Devices → Link a Device</span>, then scan
          this code.
        </p>

        {/* Consent / risk notice — R2 / R-BAN / R-PRIV. */}
        <div className="mt-6 rounded-lg border border-info-border bg-info-bg p-4">
          <p className="text-[13.5px] font-semibold text-info">Before you connect</p>
          <p className="mt-1 text-[13px] text-info/80">
            This links your personal WhatsApp to Brain over an unofficial connection — your account
            could be limited by WhatsApp. You choose which chats to index next. Chats you index are
            {visibility === 'private'
              ? ' private to you.'
              : ' searchable by your whole workspace unless you mark them private.'}
          </p>
        </div>

        {/* QR / status */}
        <div className="mt-6 flex flex-col items-center gap-4">
          {failed ? (
            <Alert variant="destructive" className="w-full">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Connection failed</AlertTitle>
              <AlertDescription>
                We couldn&apos;t link your device. Start over and try again.
              </AlertDescription>
            </Alert>
          ) : connected ? (
            <div className="flex items-center gap-2 text-success">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-medium">Connected — setting things up…</span>
            </div>
          ) : (
            <div className="flex h-[232px] w-[232px] items-center justify-center rounded-lg border border-border-subtle bg-white p-3">
              {data?.qr ? (
                <img src={qrSrc(data.qr)} alt="WhatsApp QR code" className="h-full w-full" />
              ) : (
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              )}
            </div>
          )}

          {!failed && !connected && (
            <p className="font-mono text-[12px] uppercase tracking-[0.05em] text-faint">
              {status === 'scanning' ? 'Waiting for you to scan…' : 'Preparing your code…'}
            </p>
          )}
        </div>

        {failed && (
          <Button size="cta" className="mt-6 w-full" onClick={restart}>
            Start over
          </Button>
        )}

        {/* Pairing-code fallback */}
        {!failed && !connected && (
          <div className="mt-6 border-t border-border-subtle pt-4">
            {!showPair ? (
              <button
                type="button"
                onClick={() => setShowPair(true)}
                className="text-[13px] text-faint transition-colors hover:text-foreground"
              >
                Can&apos;t scan? Pair with a code instead
              </button>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="phone">Your WhatsApp number (with country code)</Label>
                <div className="flex gap-2">
                  <Input
                    id="phone"
                    placeholder="15551234567"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="font-mono text-sm"
                  />
                  <Button
                    variant="outline"
                    disabled={phone.trim().length < 5 || pairMut.isPending || !sessionId}
                    onClick={() => pairMut.mutate()}
                  >
                    {pairMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Get code'}
                  </Button>
                </div>
                {pairCode && (
                  <InfoNote>
                    In WhatsApp → Linked Devices → Link with phone number instead, enter:{' '}
                    <span className="font-mono font-semibold">{pairCode}</span>
                  </InfoNote>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="text-[13px] text-faint transition-colors hover:text-foreground"
        >
          ← Choose a different way to connect
        </button>
      )}
    </div>
  );
}
